// Product Calculator Logic (Fixed)
// Handles both product page calculator and standalone calculator with product selector

class ProductCalculator {
  constructor(sectionId, options = {}) {
    this.section = sectionId;
    this.sectionWrapper = document.querySelector(`.t4s_nt_se_${sectionId}`);
    if (!this.sectionWrapper) return;

    // Options
    this.hasProductSelector = options.hasProductSelector || false;
    this.bulkPriceThreshold = Number(options.bulkPriceThreshold || options.bulkThreshold || 100);
    this.bulkThresholdCents = Math.max(0, this.bulkPriceThreshold) * 100;
    this.bulkDiscountPercent = Number(options.bulkDiscountPercent || 25);
    this.initialPrice = options.initialPrice || 0;
    this.initialProductTitle = options.initialProductTitle || '';

    // DOM Elements
    this.productSelector = document.getElementById(`product-selector-${this.section}`);
    this.quantityInput = document.getElementById(`product-quantity-${this.section}`);
    this.quantitySlider = document.getElementById(`product-quantity-slider-${this.section}`);
    this.emailToggle = document.getElementById(`email-quote-toggle-${this.section}`);
    this.emailForm = document.getElementById(`email-form-${this.section}`);
    this.emailInput = document.getElementById(`quote-email-${this.section}`);
    this.sendBtn = this.sectionWrapper.querySelector('.t4s-send-quote-btn');
    this.currentPriceEl = this.sectionWrapper.querySelector('.t4s-current-price');
    this.totalAmountEl = this.sectionWrapper.querySelector('.t4s-total-amount');
    this.discountChip = this.sectionWrapper.querySelector('.t4s-discount-chip');
    this.wasPrice = this.sectionWrapper.querySelector('.t4s-was-price');
    this.frontColorsSelect = document.getElementById(`front-colors-${this.section}`);
    this.backColorsSelect = document.getElementById(`back-colors-${this.section}`);
    this.decorationSelect = document.getElementById(`decoration-${this.section}`);

    // State
    this.selectedProductTitle = this.initialProductTitle;

    // Initialize
    this.init();
  }

  init() {
    // Set initial price if provided
    if (this.initialPrice && this.currentPriceEl) {
      this.currentPriceEl.dataset.price = this.initialPrice;
    }

    // Bind events
    this.bindQuantityEvents();
    this.bindEmailEvents();
    this.bindSendEmailEvent();
    this.showContactSuccessIfNeeded();

    if (this.hasProductSelector && this.productSelector) {
      this.bindProductSelectorEvent();
    } else {
      // Initialize price calculation for fixed product
      this.updatePrice();
    }

    // Initialize UI state
    this.updateSliderGradient();
    this.updateSendButtonState();
  }

  bindProductSelectorEvent() {
    if (!this.productSelector) return;
    this.productSelector.addEventListener('change', () => {
      const selectedOption = this.productSelector.options[this.productSelector.selectedIndex];
      if (selectedOption.value) {
        const price = Number.parseFloat(selectedOption.dataset.price) || 0;
        const comparePrice = Number.parseFloat(selectedOption.dataset.compare) || 0;
        this.selectedProductTitle = selectedOption.dataset.title || '';
        if (this.currentPriceEl) {
          this.currentPriceEl.dataset.price = price;
        }
        if (this.wasPrice) {
          this.wasPrice.dataset.compare = comparePrice;
        }
        this.updatePrice();
      } else {
        // Reset if no product selected
        this.resetCalculator();
      }
    });
  }

  bindQuantityEvents() {
    if (!this.quantityInput || !this.quantitySlider) return;
    this.quantityInput.addEventListener('input', () => {
      this.quantitySlider.value = this.quantityInput.value;
      this.updateSliderGradient();
      this.updatePrice();
    });
    this.quantitySlider.addEventListener('input', () => {
      this.quantityInput.value = this.quantitySlider.value;
      this.updateSliderGradient();
      this.updatePrice();
    });
  }

  bindEmailEvents() {
    if (this.emailToggle && this.emailForm) {
      this.emailToggle.addEventListener('change', () => {
        this.emailForm.style.display = this.emailToggle.checked ? 'flex' : 'none';
        this.updateSendButtonState();
      });
    }
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => {
        this.updateSendButtonState();
      });
    }
  }

  /**
   * Handles clicking the send quote button.  This method wraps the form data in
   * a temporary form and leverages Shopify's hCaptcha protection when available.
   * If hCaptcha is enabled, a hidden hCaptcha token will be added to the form by
   * window.Shopify.captcha.protect().  The resulting data is then submitted via
   * fetch() as FormData so that the payload matches a native form submission.
   * If hCaptcha is unavailable the request falls back to submitting without a
   * token.
   */
  bindSendEmailEvent() {
    if (!this.sendBtn) return;
    this.sendBtn.addEventListener('click', () => {
      if (!this.emailInput || this.sendBtn.disabled) {
        return;
      }
      // Check product selection in selector mode
      if (this.hasProductSelector && this.productSelector && !this.productSelector.value) {
        alert('Please select a product first');
        return;
      }
      const email = this.emailInput.value;
      const quantity = this.quantityInput.value;
      const frontColors = this.frontColorsSelect ? this.frontColorsSelect.value : '0';
      const backColors = this.backColorsSelect ? this.backColorsSelect.value : '0';
      const decoration = this.decorationSelect ? this.decorationSelect.value : 'printing';

      // Base parameters for the contact form
      const params = {
        form_type: 'contact',
        utf8: '✓',
        'contact[name]': `Quote request - ${this.selectedProductTitle}`,
        'contact[email]': email,
        'contact[body]': `User with email ${email} wants to get a quote for ${this.selectedProductTitle}, ${quantity} units, with:\n- Front images: ${frontColors}\n- Back images: ${backColors}\n- Decoration: ${decoration}`,
      };

      // Helper to send the request using a native form submission. If hCaptcha
      // is enabled, rely on Shopify's protect() to inject the token before
      // submitting this same form. We avoid double-form creation to ensure the
      // token stays attached to the submitted form.
      const sendRequest = () => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/contact#contact_form';
        form.style.display = 'none';
        form.setAttribute('data-quote-form', 'true');

        Object.keys(params).forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);

        const submitForm = () => {
          form.submit();
        };

        if (window.Shopify?.captcha?.protect) {
          window.Shopify.captcha.protect(form, submitForm);
        } else {
          submitForm();
        }
      };

      // If hCaptcha protection is available, create a temporary form and protect it
      if (window.Shopify?.captcha?.protect) {
          // Use the same flow as sendRequest; protect will inject the token and submit.
          sendRequest();
      } else {
        // No hCaptcha; send immediately
        sendRequest();
      }
    });
  }

  showContactSuccessIfNeeded() {
    if (!this.sectionWrapper) return;

    const params = new URLSearchParams(window.location.search || '');
    if (params.get('contact_posted') !== 'true') return;

    const content = this.sectionWrapper.querySelector('.t4s-calculator-content') || this.sectionWrapper;
    const note = document.createElement('div');
    note.className = 't4s-quote-success';
    note.style.margin = '0 20px 12px';
    note.style.padding = '14px 20px';
    note.style.borderRadius = '8px';
    note.style.background = '#e7f6ed';
    note.style.color = '#0f5132';
    note.style.fontWeight = '600';
    note.textContent = 'Thank you! Your quote request was sent.';
    content.insertAdjacentElement('afterbegin', note);

    // Clean the URL so the message does not persist on navigation
    params.delete('contact_posted');
    const newSearch = params.toString();
    const newUrl = newSearch ? `${window.location.pathname}?${newSearch}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, document.title, newUrl);
  }

  updateSliderGradient() {
    if (!this.quantitySlider) return;
    const min = Number.parseFloat(this.quantitySlider.min) || 0;
    const max = Number.parseFloat(this.quantitySlider.max) || 100;
    const value = Number.parseFloat(this.quantitySlider.value) || 0;
    const percentage = ((value - min) / (max - min)) * 100;
    this.quantitySlider.style.setProperty('--slider-progress', `${percentage}%`);
  }

  updateSendButtonState() {
    if (!this.sendBtn) return;
    const isChecked = this.emailToggle ? this.emailToggle.checked : false;
    const hasEmail = this.emailInput ? this.emailInput.value.trim().length > 0 : false;
    this.sendBtn.disabled = !(isChecked && hasEmail);
  }

  updatePrice() {
    if (!this.quantityInput || !this.currentPriceEl || !this.totalAmountEl) {
      return;
    }
    const quantity = Number.parseInt(this.quantityInput.value, 10) || 1;
    const basePrice = Number.parseFloat(this.currentPriceEl.dataset.price) || 0;
    const comparePrice = this.wasPrice ? Number.parseFloat(this.wasPrice.dataset.compare) || 0 : 0;
    if (basePrice === 0) {
      return;
    }
    const discountRate = Math.max(0, this.bulkDiscountPercent) / 100;
    const preDiscountTotal = basePrice * quantity;
    const discount = preDiscountTotal >= this.bulkThresholdCents ? discountRate : 0;
    const pricePerUnit = basePrice * (1 - discount);
    const totalPrice = pricePerUnit * quantity;
    const originalPrice = comparePrice > basePrice ? comparePrice : basePrice;
    this.currentPriceEl.textContent = `$${this.formatMoney(pricePerUnit)}`;
    this.totalAmountEl.textContent = `$${this.formatMoney(totalPrice)}`;
    if (this.discountChip) {
      this.discountChip.style.display = discount > 0 ? 'flex' : 'none';
    }
    if (this.wasPrice) {
      const showWasPrice = discount > 0 || comparePrice > basePrice;
      this.wasPrice.style.display = showWasPrice ? 'block' : 'none';
      if (showWasPrice) {
        const wasValue = discount > 0 ? basePrice : originalPrice;
        this.wasPrice.textContent = `$${this.formatMoney(wasValue)}`;
      }
    }
  }

  resetCalculator() {
    if (this.currentPriceEl) {
      this.currentPriceEl.textContent = '$0.00';
      this.currentPriceEl.dataset.price = '0';
    }
    if (this.totalAmountEl) {
      this.totalAmountEl.textContent = '$0.00';
    }
    if (this.discountChip) {
      this.discountChip.style.display = 'none';
    }
    if (this.wasPrice) {
      this.wasPrice.style.display = 'none';
    }
    this.selectedProductTitle = '';
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

// Expose to global window
window.ProductCalculator = ProductCalculator;
